import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { organizationService } from '../services/organization';

export const orgKeys = {
  all: ['organizations'] as const,
  list: () => [...orgKeys.all, 'list'] as const,
  members: (orgId: string) => [...orgKeys.all, 'members', orgId] as const,
};

export function useOrganizations() {
  return useQuery({
    queryKey: orgKeys.list(),
    queryFn: () => organizationService.list(),
  });
}

export function useCreateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; slug: string; description?: string }) =>
      organizationService.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: orgKeys.list() }),
  });
}

export function useOrgMembers(orgId: string | null) {
  return useQuery({
    queryKey: orgKeys.members(orgId ?? ''),
    queryFn: () => organizationService.members(orgId as string),
    enabled: !!orgId,
  });
}

export function useAddMember(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ email, role }: { email: string; role: string }) =>
      organizationService.addMember(orgId, email, role),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgKeys.members(orgId) });
      qc.invalidateQueries({ queryKey: orgKeys.list() });
    },
  });
}

export function useRemoveMember(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      organizationService.removeMember(orgId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgKeys.members(orgId) });
      qc.invalidateQueries({ queryKey: orgKeys.list() });
    },
  });
}
